/**
 * `RecoveryState` — recovery flow state machine per design memo PR #136 §2.
 *
 * Discriminated-union states + reducer (`applyEvent`) lifted from
 * informed-export pattern (PR #60). Linear forward progression with
 * universal `cancel` / `error` transitions reachable from any
 * non-terminal state.
 *
 * **Phase A scaffolding** — UX shell + state machine + honest copy +
 * accommodation routing + i18n. Cryptographic mechanism (recovery
 * passphrase derivation via canonical `deriveKeyFromPassphrase`;
 * threshold reconstruction client-side; ADR-032 §1 + scope memo §5.2)
 * is stubbed at this phase; substantive integration lands in Phase B
 * post-Issue-#48 counsel disposition + DEV3 backend implementation.
 *
 * **Doctrinal posture preserved at every state** per design memo §1:
 *   - ADR-026 §3 non-delegable fiduciary authority (recovery is
 *     patient-facing-account-control)
 *   - Recovery UX honesty doctrine (memory `feedback_phg_recovery_ux_honesty`)
 *   - Recovery ≠ erasure (ADR-024 §2.7.5 strict separation)
 *   - NO SHT BACKDOOR (Tech Spec §6.2.3 + memory `feedback_phg_client_active_session_crypto`)
 *
 * **Cancel/error universality** per export pattern: any non-terminal
 * state may transition to `cancelled` or `error` via the universal
 * cancel/error events; terminal states (`success`, `failure-recoverable`,
 * `failure-unrecoverable`, `cancelled`, `error`) do not transition.
 */
export const RECOVERY_TERMINAL_STATES = new Set([
    'success',
    'failure-recoverable',
    'failure-unrecoverable',
    'cancelled',
    'error',
]);
export function isTerminal(state) {
    return RECOVERY_TERMINAL_STATES.has(state.kind);
}
// ---------- Transitions ----------
export class RecoveryTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid recovery transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'RecoveryTransitionError';
    }
}
export function applyEvent(state, event) {
    // Universal cancel/error reachable from any non-terminal state
    if (!isTerminal(state)) {
        if (event.kind === 'cancel') {
            return { kind: 'cancelled', reason: event.reason };
        }
        if (event.kind === 'error') {
            return { kind: 'error', code: event.code, message: event.message };
        }
    }
    switch (state.kind) {
        case 'intro':
            if (event.kind === 'begin-tier-election') {
                return { kind: 'tier-election' };
            }
            break;
        case 'tier-election':
            if (event.kind === 'submit-tier') {
                return {
                    kind: 'verification-layers',
                    tier: event.tier,
                    sessionId: event.sessionId,
                    requiredLayers: event.requiredLayers,
                    layerStates: event.requiredLayers.map((kind) => ({ kind, status: 'not-started' })),
                    coolOffSeconds: event.coolOffSeconds,
                };
            }
            break;
        case 'verification-layers':
            if (event.kind === 'layer-status-changed') {
                return {
                    ...state,
                    layerStates: state.layerStates.map((ls) => ls.kind === event.layer
                        ? {
                            kind: ls.kind,
                            status: event.status,
                            ...(event.failureReason !== undefined
                                ? { failureReason: event.failureReason }
                                : {}),
                        }
                        : ls),
                };
            }
            if (event.kind === 'all-layers-complete') {
                return {
                    kind: 'recovery-passphrase',
                    tier: state.tier,
                    sessionId: state.sessionId,
                    coolOffSeconds: state.coolOffSeconds,
                };
            }
            if (event.kind === 'recovery-failed') {
                if (event.outcome === 'recoverable') {
                    return {
                        kind: 'failure-recoverable',
                        sessionId: state.sessionId,
                        escalationPathway: event.escalationPathway ?? 'shdt-staff-channel',
                        reason: event.reason ?? 'verification-layers-incomplete',
                    };
                }
                return { kind: 'failure-unrecoverable', sessionId: state.sessionId };
            }
            break;
        case 'recovery-passphrase':
            if (event.kind === 'submit-passphrase') {
                return {
                    kind: 'threshold-reconstruct',
                    tier: state.tier,
                    sessionId: state.sessionId,
                    coolOffSeconds: state.coolOffSeconds,
                };
            }
            if (event.kind === 'recovery-failed') {
                if (event.outcome === 'unrecoverable') {
                    return { kind: 'failure-unrecoverable', sessionId: state.sessionId };
                }
                return {
                    kind: 'failure-recoverable',
                    sessionId: state.sessionId,
                    escalationPathway: event.escalationPathway ?? 'shdt-staff-channel',
                    reason: event.reason ?? 'passphrase-derivation-failed',
                };
            }
            break;
        case 'threshold-reconstruct':
            if (event.kind === 'reconstruction-complete') {
                return {
                    kind: 'cooling-off-active',
                    tier: state.tier,
                    sessionId: state.sessionId,
                    secondsRemaining: state.coolOffSeconds,
                    coolOffUntil: event.coolOffUntil,
                };
            }
            if (event.kind === 'recovery-failed') {
                if (event.outcome === 'unrecoverable') {
                    return { kind: 'failure-unrecoverable', sessionId: state.sessionId };
                }
                return {
                    kind: 'failure-recoverable',
                    sessionId: state.sessionId,
                    escalationPathway: event.escalationPathway ?? 'shdt-staff-channel',
                    reason: event.reason ?? 'threshold-reconstruction-failed',
                };
            }
            break;
        case 'cooling-off-active':
            if (event.kind === 'cool-off-tick') {
                return { ...state, secondsRemaining: event.secondsRemaining };
            }
            if (event.kind === 'cool-off-elapsed') {
                return {
                    kind: 'success',
                    tier: state.tier,
                    sessionId: state.sessionId,
                };
            }
            break;
        default:
            // terminal states; no further transitions
            break;
    }
    throw new RecoveryTransitionError(state.kind, event.kind);
}
// ---------- Helpers ----------
export function allLayersCompleted(state) {
    return state.layerStates.every((ls) => ls.status === 'completed');
}
//# sourceMappingURL=state.js.map